import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    CheckCircle2,
    Loader2,
    XCircle,
    ArrowRight,
    PackageSearch,
    Receipt,
    Sprout,
    MapPin,
    Info,
} from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { PickupActions } from "../components/PickupActions";
import { CHECKOUT } from "../lib/testIds";
import { getPaymentStatus } from "../lib/api";

const POLL_INTERVAL = 2000;
const POLL_MAX = 15;

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

export const CheckoutSuccess = () => {
    const [search] = useSearchParams();
    const sessionId = search.get("session_id");
    const [state, setState] = useState({ phase: "polling", attempts: 0, txn: null });
    const [pickupOpen, setPickupOpen] = useState(false);
    const pickupPoppedRef = useRef(false);
    const navigate = useNavigate();
    const cancelled = useRef(false);

    useEffect(() => {
        cancelled.current = false;
        if (!sessionId) {
            setState({ phase: "error", attempts: 0, txn: null });
            return;
        }
        let attempts = 0;
        const tick = async () => {
            if (cancelled.current) return;
            attempts += 1;
            try {
                const txn = await getPaymentStatus(sessionId);
                if (cancelled.current) return;
                if (txn.payment_status === "paid") {
                    setState({ phase: "paid", attempts, txn });
                    // Auto-open the pickup dialog once, on first paid detection.
                    if (!pickupPoppedRef.current && (txn.item_full_address || txn.item_pickup_instructions)) {
                        pickupPoppedRef.current = true;
                        setPickupOpen(true);
                    }
                    return;
                }
                if (txn.status === "expired" || txn.payment_status === "failed") {
                    setState({ phase: "failed", attempts, txn });
                    return;
                }
                if (attempts >= POLL_MAX) {
                    setState({ phase: "timeout", attempts, txn });
                    return;
                }
                setState({ phase: "polling", attempts, txn });
                setTimeout(tick, POLL_INTERVAL);
            } catch (err) {
                if (cancelled.current) return;
                setState({ phase: "error", attempts, txn: null });
            }
        };
        tick();
        return () => {
            cancelled.current = true;
        };
    }, [sessionId]);

    const txn = state.txn;
    const address = txn?.item_full_address;
    const pickupNotes = txn?.item_pickup_instructions;
    const ownerName = txn?.owner_name;

    return (
        <main
            data-testid={CHECKOUT.successRoot}
            className="relative z-10 mx-auto max-w-2xl px-6 py-20 md:px-10"
        >
            <div className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-10 text-center shadow-sm">
                {state.phase === "polling" && (
                    <>
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d]/10 text-[#737a3d]">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                        <h1 className="mt-5 font-display text-3xl font-bold text-[#2e3220]">
                            Confirming your payment…
                        </h1>
                        <p
                            data-testid={CHECKOUT.statusBadge}
                            className="mt-2 text-sm text-[#5a6142]"
                        >
                            Checking with Stripe (attempt {state.attempts}/{POLL_MAX})
                        </p>
                    </>
                )}

                {state.phase === "paid" && (
                    <>
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                            <CheckCircle2 className="h-7 w-7" strokeWidth={2.25} />
                        </div>
                        <h1 className="mt-5 font-display text-3xl font-bold text-[#2e3220]">
                            You&apos;ve rented {state.txn?.item_name || "this item"}!
                        </h1>
                        <p
                            data-testid={CHECKOUT.statusBadge}
                            className="mt-2 text-sm text-[#5a6142]"
                        >
                            Stripe confirmed your payment. We&apos;ve added this rental to your
                            dashboard.
                        </p>

                        <div
                            data-testid={CHECKOUT.receipt}
                            className="mt-7 rounded-2xl border border-[#2e3220]/10 bg-[#E8E2D0]/50 p-5 text-left"
                        >
                            <p className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                <Receipt className="h-3.5 w-3.5" />
                                Receipt
                            </p>
                            <dl className="mt-3 space-y-1.5 text-sm text-[#2e3220]">
                                <div className="flex justify-between">
                                    <dt className="text-[#5a6142]">Item</dt>
                                    <dd className="font-semibold">{state.txn?.item_name}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-[#5a6142]">Days</dt>
                                    <dd className="font-semibold">{state.txn?.days}</dd>
                                </div>
                                <div className="flex justify-between">
                                    <dt className="text-[#5a6142]">Charged</dt>
                                    <dd className="font-display text-base font-bold">
                                        {formatMoney(state.txn?.amount)}
                                    </dd>
                                </div>
                                <div className="flex justify-between text-[11px] text-[#5a6142]/80">
                                    <dt>Lendly platform fee (10%)</dt>
                                    <dd>{formatMoney(state.txn?.platform_fee)}</dd>
                                </div>
                                <div className="flex justify-between text-[11px] text-[#5a6142]/80">
                                    <dt>Owner payout (90%)</dt>
                                    <dd>{formatMoney(state.txn?.payout_amount)}</dd>
                                </div>
                            </dl>
                        </div>

                        {(address || pickupNotes) && (
                            <div
                                data-testid="checkout-pickup-card"
                                className="mt-4 rounded-2xl border-2 border-[#737a3d]/30 bg-[#737a3d]/5 p-5 text-left"
                            >
                                <p className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    <MapPin className="h-3.5 w-3.5" strokeWidth={2.5} />
                                    Pickup details
                                </p>
                                {address && (
                                    <p
                                        data-testid="checkout-pickup-address"
                                        className="mt-2 font-display text-base font-bold text-[#2e3220]"
                                    >
                                        {address}
                                    </p>
                                )}
                                {pickupNotes && (
                                    <p
                                        data-testid="checkout-pickup-instructions"
                                        className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-[#2e3220]"
                                    >
                                        {pickupNotes}
                                    </p>
                                )}
                                {ownerName && (
                                    <p className="mt-3 text-[11px] text-[#5a6142]">
                                        From <span className="font-semibold">{ownerName}</span>
                                    </p>
                                )}
                                <PickupActions
                                    address={address}
                                    testIdPrefix="checkout-pickup-card"
                                />
                            </div>
                        )}

                        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={() => navigate("/dashboard/rentals")}
                                data-testid={CHECKOUT.goRentalsBtn}
                                className="btn-primary"
                            >
                                See what I'm borrowing
                                <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                            </button>
                            <Link
                                to="/browse"
                                data-testid={CHECKOUT.goBrowseBtn}
                                className="btn-secondary"
                            >
                                Browse more
                            </Link>
                        </div>
                    </>
                )}

                {(state.phase === "failed" ||
                    state.phase === "timeout" ||
                    state.phase === "error") && (
                    <>
                        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#B56C5A]/15 text-[#B56C5A]">
                            <XCircle className="h-7 w-7" />
                        </div>
                        <h1 className="mt-5 font-display text-3xl font-bold text-[#2e3220]">
                            {state.phase === "timeout"
                                ? "Still confirming…"
                                : "We couldn't confirm your payment"}
                        </h1>
                        <p
                            data-testid={CHECKOUT.statusBadge}
                            className="mt-2 text-sm text-[#5a6142]"
                        >
                            {state.phase === "timeout"
                                ? "Stripe is taking longer than usual. Check Borrowing shortly — it&apos;ll appear automatically."
                                : "If you were charged, the rental will still appear in Borrowing. No need to retry."}
                        </p>
                        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                            <Link
                                to="/dashboard/rentals"
                                data-testid={CHECKOUT.goRentalsBtn}
                                className="btn-primary"
                            >
                                Go to Borrowing
                            </Link>
                            <Link
                                to="/browse"
                                data-testid={CHECKOUT.goBrowseBtn}
                                className="btn-secondary"
                            >
                                Back to Browse
                            </Link>
                        </div>
                    </>
                )}
            </div>

            <p className="mt-6 text-center text-xs text-[#5a6142]">
                <Sprout className="mr-1 inline h-3 w-3 text-[#737a3d]" />
                Thanks for being a Lendly Neighbor.
            </p>

            <Dialog open={pickupOpen} onOpenChange={setPickupOpen}>
                <DialogContent
                    data-testid="checkout-pickup-dialog"
                    className="max-w-md rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-0 text-[#2e3220]"
                >
                    <div className="p-6">
                        <DialogHeader className="text-left">
                            <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                                <MapPin className="h-5 w-5" strokeWidth={2.5} />
                            </div>
                            <DialogTitle className="font-display text-2xl font-bold text-[#2e3220]">
                                You&apos;re all set — here&apos;s the pickup
                            </DialogTitle>
                            <DialogDescription className="text-sm text-[#5a6142]">
                                {ownerName
                                    ? `${ownerName} shared the pickup details with you.`
                                    : "The lender shared the pickup details with you."}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="mt-5 space-y-3">
                            {address && (
                                <div className="rounded-2xl border border-[#2e3220]/10 bg-white/60 p-4">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                        Address
                                    </p>
                                    <p
                                        data-testid="checkout-pickup-dialog-address"
                                        className="mt-1 font-display text-lg font-bold text-[#2e3220]"
                                    >
                                        {address}
                                    </p>
                                    <PickupActions
                                        address={address}
                                        testIdPrefix="checkout-pickup-dialog"
                                    />
                                </div>
                            )}
                            {pickupNotes && (
                                <div className="rounded-2xl border border-[#2e3220]/10 bg-white/60 p-4">
                                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                        Pickup instructions
                                    </p>
                                    <p
                                        data-testid="checkout-pickup-dialog-instructions"
                                        className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-[#2e3220]"
                                    >
                                        {pickupNotes}
                                    </p>
                                </div>
                            )}
                            <p className="flex items-start gap-2 rounded-2xl bg-[#2e3220]/5 px-3 py-2 text-[11px] text-[#5a6142]">
                                <Info className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                We also emailed these details and saved them to your notifications, so you can find them anytime.
                            </p>
                        </div>
                        <DialogFooter className="mt-5">
                            <Button
                                data-testid="checkout-pickup-dialog-close"
                                onClick={() => setPickupOpen(false)}
                                className="w-full bg-[#737a3d] text-[#F4F1EA] hover:bg-[#5a6142]"
                            >
                                Got it
                            </Button>
                        </DialogFooter>
                    </div>
                </DialogContent>
            </Dialog>
        </main>
    );
};

export const CheckoutCancel = () => {
    const [search] = useSearchParams();
    const itemId = search.get("item_id");
    return (
        <main
            data-testid={CHECKOUT.cancelRoot}
            className="relative z-10 mx-auto max-w-2xl px-6 py-20 md:px-10"
        >
            <div className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-10 text-center shadow-sm">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#2e3220]/10 text-[#2e3220]">
                    <PackageSearch className="h-7 w-7" />
                </div>
                <h1 className="mt-5 font-display text-3xl font-bold text-[#2e3220]">
                    Checkout cancelled
                </h1>
                <p className="mt-2 text-sm text-[#5a6142]">
                    No worries — you weren&apos;t charged. The item&apos;s still on Browse if
                    you change your mind.
                </p>
                <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
                    <Link
                        to="/browse"
                        data-testid={CHECKOUT.goBrowseBtn}
                        className="btn-primary"
                    >
                        Back to Browse
                    </Link>
                    {itemId && (
                        <Link
                            to={`/browse`}
                            data-testid={CHECKOUT.retryBtn}
                            className="btn-secondary"
                        >
                            Try again
                        </Link>
                    )}
                </div>
            </div>
        </main>
    );
};
